import { ArrowLeft, LockKeyhole } from 'lucide-react';
import { useEffect, useId, useRef } from 'react';

import { Button } from '@/components/lumen/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/lumen/dialog';
import { Separator } from '@/components/lumen/separator';
import { useLanguage } from '@contexts/LanguageContext';

import { MFACodeInput } from './MFACodeInput';

/**
 * The second step of a sign-in for a user with MFA (the old page's inline verify modal). The page
 * owns the code and submits it itself when the sixth digit lands. It cannot be clicked away;
 * Escape does what "Kembali ke Login" does, except while a code is being checked.
 */
export function MFAVerifyDialog({
  open,
  code,
  onCodeChange,
  isVerifying,
  error,
  onBack,
}: {
  open: boolean;
  code: string;
  onCodeChange: (value: string) => void;
  isVerifying: boolean;
  /** Why the last code was refused (the server's message when it sent one). */
  error?: string;
  onBack: () => void;
}) {
  const { t } = useLanguage();
  const errorId = useId();
  const inputRef = useRef<HTMLInputElement>(null);

  // A refused code is cleared and the boxes come back enabled: put the caret back in them.
  useEffect(() => {
    if (open && error && !isVerifying) inputRef.current?.focus();
  }, [open, error, isVerifying]);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !isVerifying) onBack();
      }}
    >
      <DialogContent
        showCloseButton={false}
        className="gap-6 py-8 sm:max-w-md"
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
      >
        <DialogHeader className="items-center text-center sm:text-center">
          <div className="mb-2 flex size-14 items-center justify-center rounded-full bg-label/12 text-label">
            <LockKeyhole className="size-7" aria-hidden="true" />
          </div>
          <DialogTitle className="text-xl">{t('lumen.auth.verify.title')}</DialogTitle>
          <DialogDescription className="max-w-sm">{t('lumen.auth.verify.description')}</DialogDescription>
        </DialogHeader>

        <div className="grid justify-items-center gap-3">
          <MFACodeInput
            ref={inputRef}
            size="xl"
            value={code}
            onChange={onCodeChange}
            disabled={isVerifying}
            invalid={Boolean(error)}
            describedBy={error ? errorId : undefined}
          />
          {isVerifying && (
            <p className="text-fg-muted text-sm" role="status">
              {t('lumen.auth.verify.verifying')}
            </p>
          )}
          {error && !isVerifying && (
            <p id={errorId} className="text-center font-medium text-err text-sm" role="alert">
              {error}
            </p>
          )}
        </div>

        <p className="text-center text-fg-muted text-xs">{t('lumen.auth.verify.help')}</p>

        <Separator />
        <Button variant="ghost" className="w-full" onClick={onBack} disabled={isVerifying}>
          <ArrowLeft aria-hidden="true" />
          {t('lumen.auth.verify.back')}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
